import { addCommas, maskAddress } from '@/utils';
import { Pagination } from '@bfe/moly';
import { Table } from '@bfe/moly';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { getBountyListApi } from '@/services/home';
import { useToast } from '@bfe/moly';
import { Empty } from '@bfe/moly';
import { Spin } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { useWindowSize } from 'react-use';
import { CommonLoading } from '@/components/CommonLoading';

export default memo(function Bounty() {
  const t = useFm();
  const { toast, EToastType } = useToast();
  const { width } = useWindowSize();

  const [queryParams, setQueryParams] = useState({
    entityName: '',
    page: 1,
    pageSize: 0,
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!queryParams.pageSize && width) {
      // 初始设置
      setQueryParams((pre) => ({ ...pre, pageSize: width <= 768 ? 4 : 6 }));
    } else if (width <= 768 && queryParams.pageSize === 6) {
      // 切换到H5请求4条数据
      setQueryParams((pre) => ({ ...pre, page: 1, pageSize: 4 }));
    } else if (width > 768 && queryParams.pageSize === 4) {
      // 切换到web请求6条数据
      setQueryParams((pre) => ({ ...pre, page: 1, pageSize: 6 }));
    }
  }, [queryParams.pageSize, width]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ranking',
        header: () => <div className="w-[220px]">{t('rank')}</div>,
        cell: ({ row }) => (
          <div className="h-[47px] flex items-center">
            🏆 {row.getValue('ranking')}
          </div>
        ),
      },
      {
        accessorKey: 'reporterName',
        header: () => <div className="w-[170px]">{t('contributors3')}</div>,
        cell: ({ row }) => {
          const { reporterName } = row?.original || {};
          // 如果 reporterName 是地址，为了不暴露用户信息，后端会做脱敏省略
          // if (reporterName?.length > 10) {
          //   return (
          //     <div>
          //       {maskAddress({
          //         address: reporterName,
          //         before: 10,
          //         after: 4,
          //       })}
          //     </div>
          //   );
          // }
          return <div>{reporterName}</div>;
        },
      },
      {
        accessorKey: 'verifiedReportNum',
        header: () => <div className="w-[180px]">{t('verifiedReports')}</div>,
      },
      {
        accessorKey: 'reportFunds',
        header: () => <div className="w-[200px]">{t('fundAwaitResponse')}</div>,
        cell: ({ row }) => <div>${addCommas(row.getValue('reportFunds'))}</div>,
      },
      {
        accessorKey: 'blockFunds',
        header: () => <div className="w-[130px]">{t('blockedFunds')}</div>,
        cell: ({ row }) => <div>${addCommas(row.getValue('blockFunds'))}</div>,
      },
      {
        accessorKey: 'estBounty',
        header: () => <div className="text-right">{t('estimateBounty')}</div>,
        cell: ({ row }) => (
          <div className="text-right">
            ${addCommas(row.getValue('estBounty'))}
          </div>
        ),
      },
    ],
    [t]
  );

  const { data = [], loading } = useRequest(
    async () => {
      if (!queryParams.pageSize) return [];
      const { success, result, message } = await getBountyListApi(queryParams);
      if (!success) {
        toast({
          iconType: EToastType.FAIL,
          title: message,
        });
        return;
      }
      setTotal(result.pageInfo.totalNum);
      return result.dataList ?? [];
    },
    {
      refreshDeps: [queryParams],
    }
  );

  const handlePageChange = useCallback((current) => {
    setQueryParams((pre) => ({ ...pre, page: current }));
  }, []);

  return (
    <div className="flex flex-col min-[768px]:mt-4 max-[768px]:gap-2 max-[768px]:mt-3 max-[768px]:h-[800px]">
      <div className="max-[768px]:hidden">
        <CommonLoading loading={loading}>
          <Table
            columns={columns}
            data={data}
            height="528px"
            emptyText={t('noRecords')}
          />
        </CommonLoading>
      </div>
      <div className="md:hidden max-w-[100vw] flex-1 h-[744px]">
        <CommonLoading loading={loading}>
          <div className="flex flex-col gap-3">
            {data.length ? (
              data.map((item, index) => (
                <div
                  key={index}
                  className="bg-bds-gray-bg-card rounded-lg p-3 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('rank')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        🏆 {item.ranking}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 text-right">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('contributors3')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        {/* 不暴露用户信息，后端会做脱敏省略 */}
                        {item.reporterName}
                        {/* {item.reporterName?.length > 10
                        ? maskAddress({
                            address: item.reporterName,
                            before: 10,
                            after: 4,
                          })
                        : item.reporterName} */}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('verifiedReports')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        {item.verifiedReportNum}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 text-right">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('fundAwaitResponse')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        ${addCommas(item.reportFunds)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('blockedFunds')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        ${addCommas(item.blockFunds)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 text-right">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {t('estimateBounty')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        ${addCommas(item.estBounty)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="min-h-[200px]">
                {!loading && <Empty subTitle={t('noRecords')}></Empty>}
              </div>
            )}
          </div>
        </CommonLoading>
      </div>
      {total > queryParams.pageSize && (
        <div className="md:mt-8 max-[768px]:mt-2 flex items-center justify-center max-w-[100vw]">
          <Pagination
            defaultCurrent={1}
            current={queryParams.page}
            pageSize={queryParams.pageSize}
            total={total}
            onChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
});
